/**
 * Herd vocabulary: stable enum values (stored forever) and their English labels (catalog keys).
 * Pure data shared by the contract, the server and the web.
 */

export const ARRIVAL_KIND = {
  purchased: "Bought",
  received: "Received",
  leased_in: "Leased in",
  returned: "Came back",
  born_elsewhere: "Born elsewhere",
} as const;
export type ArrivalKind = keyof typeof ARRIVAL_KIND;

export const DEPARTURE_KIND = {
  sold: "Sold",
  shipped: "Shipped (still ours)",
  transferred: "Transferred",
  leased_out: "Leased out",
  slaughtered: "Butchered",
} as const;
export type DepartureKind = keyof typeof DEPARTURE_KIND;

/** What each way of leaving does to the animal's status. Shipped and leased-out cattle are still ours, just not here. */
export const DEPARTURE_STATUS: Record<DepartureKind, "sold" | "transferred" | "dead"> = {
  sold: "sold",
  shipped: "transferred",
  transferred: "transferred",
  leased_out: "transferred",
  slaughtered: "dead",
};

/** Whether the ranch still holds title after this kind of departure. */
export const DEPARTURE_KEEPS_TITLE: Record<DepartureKind, boolean> = {
  sold: false,
  shipped: true,
  transferred: false,
  leased_out: true,
  slaughtered: true,
};

export const SALE_REASON = {
  market: "Market sale",
  breeding: "Breeding stock",
  cull: "Cull",
  other: "Other",
} as const;

export const PRICE_BASIS = {
  per_head: "A head",
  per_cwt: "Per hundredweight",
  per_lb: "Per pound",
  total: "Total",
} as const;
export type PriceBasis = keyof typeof PRICE_BASIS;

export const DEATH_CAUSE = {
  unknown: "Not known",
  calving: "Calving trouble",
  sickness: "Sickness",
  injury: "Injury or accident",
  predator: "Predator",
  weather: "Weather or cold",
  bloat: "Bloat",
  poison: "Poisonous plants",
  lightning: "Lightning",
  old_age: "Old age",
  euthanized: "Put down",
  other: "Other",
} as const;
export type DeathCause = keyof typeof DEATH_CAUSE;

export const CAUSE_BASIS = {
  seen: "Saw it happen",
  vet: "Vet said so",
  necropsy: "Necropsy",
  suspected: "Best guess",
} as const;

export const DISPOSAL = {
  buried: "Buried",
  rendered: "Rendering truck",
  composted: "Composted",
  burned: "Burned",
  left: "Left in the pasture",
  other: "Other",
} as const;

export const CULL_REASON = {
  open: "Open",
  late: "Calved late",
  age: "Age or bad mouth",
  feet: "Feet or legs",
  udder: "Udder or teats",
  eyes: "Eyes",
  disposition: "Disposition",
  health: "Health",
  poor_calf: "Poor calves",
  prolapse: "Prolapse",
  other: "Other",
} as const;
export type CullReason = keyof typeof CULL_REASON;

export const CULL_DECISION = {
  cull: "Cull",
  keep: "Keep after all",
} as const;

export const ID_ACTION = {
  retag: "New tag or number",
  add: "Add an ID",
  lost: "Lost tag",
  remove: "Take off an ID",
} as const;
export type IdAction = keyof typeof ID_ACTION;

export const ID_SCHEME = {
  visual: "Ear tag",
  eid: "EID",
  registration: "Registration number",
  tattoo: "Tattoo",
  brand: "Brand",
  herd_id: "Herd number",
  other: "Other ID",
} as const;
export type IdScheme = keyof typeof ID_SCHEME;

export const TAG_REASON = {
  lost: "Lost tag",
  torn: "Torn or hard to read",
  renumber: "New numbering",
  bought: "Came with another ranch's tag",
  typo: "Fixing a typo",
  other: "Other",
} as const;

export const EAR = {
  left: "Left ear",
  right: "Right ear",
} as const;

export const INTEREST = {
  title: "Ownership",
  possession: "Possession",
  genetics: "Genetic interest",
  semen: "Semen rights",
  revenue: "Share of proceeds",
} as const;
export type InterestKind = keyof typeof INTEREST;

export const OWNERSHIP_BASIS = {
  told: "Told to us",
  paperwork: "Have the paperwork",
  disputed: "Disputed",
} as const;

export const WEAN_METHOD = {
  fenceline: "Fence-line",
  abrupt: "Abrupt",
  two_step: "Two-step (nose flaps)",
  sold: "Sold off the cow",
  other: "Other",
} as const;

export const LOT_ACTION = {
  join: "Into the lot",
  leave: "Out of the lot",
  move: "To another lot",
} as const;

export const LOT_PURPOSE = {
  production: "Production lot",
  ownership: "Ownership lot",
  custom: "Someone else's cattle",
} as const;

/** "{{count}} steers" phrases by class, for summaries. Collected for the catalog through the contract's messages. */
export const CLASS_COUNT: Record<string, [string, string]> = {
  cow: ["{{count}} cow", "{{count}} cows"],
  heifer: ["{{count}} heifer", "{{count}} heifers"],
  bull: ["{{count}} bull", "{{count}} bulls"],
  steer: ["{{count}} steer", "{{count}} steers"],
  calf: ["{{count}} calf", "{{count}} calves"],
  bull_calf: ["{{count}} bull calf", "{{count}} bull calves"],
  heifer_calf: ["{{count}} heifer calf", "{{count}} heifer calves"],
  yearling: ["{{count}} yearling", "{{count}} yearlings"],
  stocker: ["{{count}} stocker", "{{count}} stockers"],
  feeder: ["{{count}} feeder", "{{count}} feeders"],
  other: ["{{count}} head", "{{count}} head"],
};
